import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SpeedIcon from '@mui/icons-material/Speed';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ShortcutHint } from './ShortcutHint';
import * as styles from './SimilarTracksModal.styles';
import type { SoundReason } from './types';

interface SoundReasonDialogProps {
  open: boolean;
  trackTitle: string;
  otherText: string;
  showOtherInput: boolean;
  onClose: () => void;
  onSelectReason: (reason: SoundReason) => void;
  onOtherTextChange: (text: string) => void;
  onOtherSubmit: () => void;
}

export const SoundReasonDialog = ({
  open,
  trackTitle,
  otherText,
  showOtherInput,
  onClose,
  onSelectReason,
  onOtherTextChange,
  onOtherSubmit,
}: SoundReasonDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>What's the sound-related issue?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={styles.dialogContentText}>
          Please select the specific sound-related reason why "{trackTitle}"
          isn't a good match:
        </DialogContentText>
        <Box sx={styles.feedbackButtonsContainer}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => onSelectReason('tempo')}
            startIcon={<SpeedIcon />}
            sx={styles.feedbackButton}
          >
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Tempo
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The tempo/pace doesn't match
              </Typography>
              <ShortcutHint label="t" />
            </Box>
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => onSelectReason('style')}
            startIcon={<LibraryMusicIcon />}
            sx={styles.feedbackButton}
          >
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Style
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The musical style doesn't match
              </Typography>
              <ShortcutHint label="s" />
            </Box>
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => onSelectReason('mood')}
            startIcon={<EmojiEmotionsIcon />}
            sx={styles.feedbackButton}
          >
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Mood
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The mood/feeling doesn't match
              </Typography>
              <ShortcutHint label="m" />
            </Box>
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => onSelectReason('other')}
            startIcon={<MoreHorizIcon />}
            sx={styles.feedbackButton}
          >
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Other
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Another sound-related reason (please specify)
              </Typography>
              <ShortcutHint label="o" />
            </Box>
          </Button>
          {showOtherInput && (
            <Box sx={styles.otherInputContainer}>
              <TextField
                fullWidth
                label="Please specify the reason"
                placeholder="e.g., Instrumentation, Production quality, etc."
                value={otherText}
                onChange={(e) => onOtherTextChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (otherText.trim()) {
                      onOtherSubmit();
                    }
                  }
                }}
                multiline
                rows={3}
                autoFocus
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {showOtherInput && (
          <Button
            onClick={onOtherSubmit}
            variant="contained"
            disabled={!otherText.trim()}
          >
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
